// components/CountryCodePicker.js
//
// The country code beside a separate phone number field (login, phone
// verification). Offers shared/authOptions PHONE_COUNTRIES - the same list
// as the email-or-phone field - and works in its entries: `value` is one,
// `onChange` receives one. Chosen by country rather than dial code, so the
// US and Canada (+1 both) are told apart. A null `value` shows a blank
// "Code" to pick; `nudge` (hooks/useCountryNudge) shakes and rings it when
// the form is sent without one.

import React, { useState } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { PHONE_COUNTRIES } from '../shared/authOptions';
import '../styles/CountryCodePicker.scss';

const CountryCodePicker = ({ value, onChange, nudge }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedCountry = PHONE_COUNTRIES.find((c) => c.iso2 === value?.iso2) || null;

  const query = search.trim().toLowerCase().replace(/^\+/, '');
  const filteredCountries = PHONE_COUNTRIES.filter((country) =>
    country.name.toLowerCase().includes(query) || country.code.startsWith(query)
  );

  const handleSelect = (country) => {
    onChange(country);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="country-picker">
      <button
        ref={nudge?.ref}
        type="button"
        className={`country-picker-trigger ${nudge?.className ?? ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={selectedCountry ? `Country code: ${selectedCountry.name} +${selectedCountry.code}` : 'Pick your country code'}
      >
        <span className="country-flag">{selectedCountry?.flag ?? '🌐'}</span>
        <span className="country-code">{selectedCountry ? `+${selectedCountry.code}` : 'Code'}</span>
        <FiChevronDown />
      </button>

      {isOpen && (
        <>
          <div className="country-picker-overlay" onClick={() => setIsOpen(false)} />
          <div className="country-picker-dropdown">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                placeholder="Search country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="country-list" role="listbox">
              {filteredCountries.map((country) => (
                <button
                  key={country.iso2}
                  type="button"
                  role="option"
                  aria-selected={country.iso2 === selectedCountry?.iso2}
                  className="country-item"
                  onClick={() => handleSelect(country)}
                >
                  <span className="country-flag">{country.flag}</span>
                  <span className="country-name">{country.name}</span>
                  <span className="country-code">+{country.code}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CountryCodePicker;
